import { Alert, Card, Skeleton, Stack, Table, Text } from "@mantine/core";
import { IconAlertTriangle, IconReceiptOff } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { PlatformInvoiceApi } from "../../../../apis";
import type { PlatformInvoice } from "../../../../apis/platformInvoice";
import { EmptyState } from "../../../../components/billing/EmptyState";
import { InvoiceStatusBadge } from "../../../../components/billing/InvoiceStatusBadge";
import { MoneyCell } from "../../../../components/billing/MoneyCell";

const invoiceTypeLabel: Record<string, string> = {
  SUBSCRIPTION_ONLY: "Захиалга",
  USAGE_ONLY: "Хэрэглээ",
  COMBINED: "Нэгдсэн"
};

export function TenantInvoicesTab({ tenantId }: { tenantId: string }) {
  const navigate = useNavigate();

  const { data, isLoading, error } = useSWR(
    `swr.billing.tenant.invoices.${tenantId}`,
    () => PlatformInvoiceApi.list(tenantId, { page: 1, limit: 50 })
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = data as any;
  const rows: PlatformInvoice[] = raw?.rows ?? (Array.isArray(raw) ? raw : []);

  if (isLoading) return <Skeleton height={220} radius="md" />;
  if (error) {
    return <Alert color="red" icon={<IconAlertTriangle size={18} />}>Нэхэмжлэх ачааллахад алдаа гарлаа.</Alert>;
  }
  if (rows.length === 0) {
    return (
      <Card withBorder radius="md" padding="md">
        <EmptyState icon={<IconReceiptOff size={26} />} message="Нэхэмжлэх олдсонгүй." />
      </Card>
    );
  }

  return (
    <Stack gap="md">
      <Card withBorder radius="md" padding={0}>
        <Table.ScrollContainer minWidth={560}>
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Нэхэмжлэх №</Table.Th>
                <Table.Th>Төрөл</Table.Th>
                <Table.Th>Хугацаа</Table.Th>
                <Table.Th ta="right">Дүн</Table.Th>
                <Table.Th>Төлөв</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((inv) => (
                <Table.Tr
                  key={inv.id}
                  onClick={() => navigate(`/billing/invoices/${inv.id}`)}
                  style={{ cursor: "pointer" }}
                >
                  <Table.Td><Text style={{ fontVariantNumeric: "tabular-nums" }}>{inv.invoiceNumber}</Text></Table.Td>
                  <Table.Td><Text size="sm" c="dimmed">{invoiceTypeLabel[inv.invoiceType] ?? inv.invoiceType}</Text></Table.Td>
                  <Table.Td><Text style={{ fontVariantNumeric: "tabular-nums" }}>{inv.periodStart ? inv.periodStart.slice(0, 7) : "—"}</Text></Table.Td>
                  <Table.Td ta="right"><MoneyCell value={inv.totalAmount} currency={inv.currency} fw={500} /></Table.Td>
                  <Table.Td><InvoiceStatusBadge status={inv.status} /></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
      <Text size="sm" c="dimmed">Нийт: {rows.length}</Text>
    </Stack>
  );
}
